import React, { useState } from 'react';
import { CONTACT_INFO, getWhatsAppBookingLink } from '../config/contactConfig';
import { RUNTIME_CONFIG } from '../config/runtimeConfig';
import { submitBookingLead } from '../services/leadCaptureService';

const toNumberPrice = (value, fallback = 50000) => {
  if (typeof value === 'number') {
    return value;
  }
  const parsed = parseInt(String(value || '').replace(/[^\d]/g, ''), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const resolveModalProfile = (service) => {
  const text = String(service?.title || service?.name || '').toLowerCase();
  if (/wedding|engagement|ring|mehendi/.test(text)) {
    return { premiumName: 'Celebration Plus', luxuryName: 'Royal Wedding' };
  }
  if (/festival|diwali|navratri|holi|janmashtami/.test(text)) {
    return { premiumName: 'Festival Grand', luxuryName: 'Festival Elite' };
  }
  if (/griha|vastu|home|pooja|lakshmi|ganpati/.test(text)) {
    return { premiumName: 'Home Harmony Plus', luxuryName: 'Divine Home Elite' };
  }
  if (/mundan|annaprashan|naming|thread|life/.test(text)) {
    return { premiumName: 'Family Blessings Plus', luxuryName: 'Milestone Signature' };
  }
  if (/spiritual|katha|hanuman|bhagwat|rudra/.test(text)) {
    return { premiumName: 'Spiritual Deep Dive', luxuryName: 'Anushthan Signature' };
  }
  return { premiumName: 'Enhanced Package', luxuryName: 'Signature Package' };
};

const BookingModal = ({ isOpen, onClose, service, onConfirmBooking }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('basic');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [errors, setErrors] = useState({});
  const [leadSubmitting, setLeadSubmitting] = useState(false);

  const normalizedService = {
    ...service,
    title: service?.title || service?.name || 'Service Booking',
    image: service?.image || service?.icon || '🕉️',
    duration: service?.duration || '2-3 hours',
    rating: service?.rating ?? '-',
    reviews: service?.reviews ?? service?.reviewCount ?? 0,
    description: service?.description || service?.detailedDescription || '',
    features: Array.isArray(service?.features)
      ? service.features
      : (Array.isArray(service?.includedServices) ? service.includedServices : [])
  };

  const basePrice = toNumberPrice(service?.price, 50000);
  const modalProfile = resolveModalProfile(normalizedService);

  // Package options
  const packages = {
    basic: {
      name: "Basic Package",
      price: basePrice,
      features: normalizedService.features
    },
    premium: {
      name: modalProfile.premiumName,
      price: Math.round(basePrice * 1.35),
      features: [...normalizedService.features, "Photography", "Videography", "Premium Decorations"]
    },
    luxury: {
      name: modalProfile.luxuryName,
      price: Math.round(basePrice * 1.75),
      features: [...normalizedService.features, "Photography", "Videography", "Premium Decorations", "Wedding Planner"]
    }
  };

  // Available time slots
  const timeSlots = [
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
    '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  const formatPrice = (price) => {
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const handleInputChange = (field, value) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!customerInfo.name.trim()) newErrors.name = 'Name is required';
    if (!customerInfo.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) newErrors.email = 'Invalid email format';
    if (!customerInfo.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(customerInfo.phone.replace(/\D/g, ''))) newErrors.phone = 'Phone number must be 10 digits';
    if (!selectedDate) newErrors.date = 'Please select a date';
    if (!selectedTime) newErrors.time = 'Please select a time';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (validateForm()) {
      setLeadSubmitting(true);
      const bookingData = {
        service: normalizedService,
        package: packages[selectedPackage],
        date: selectedDate,
        time: selectedTime,
        customerInfo,
        totalPrice: packages[selectedPackage].price
      };

      let leadResult = null;
      if (RUNTIME_CONFIG.LEAD_CAPTURE_ENABLED) {
        try {
          leadResult = await submitBookingLead(
            {
              source: 'booking-modal',
              serviceTitle: normalizedService.title,
              packageName: packages[selectedPackage].name,
              totalPrice: packages[selectedPackage].price,
              eventDate: selectedDate,
              eventTime: selectedTime,
              customerName: customerInfo.name,
              customerPhone: customerInfo.phone,
              customerEmail: customerInfo.email,
              address: customerInfo.address,
              city: '',
              notes: normalizedService.description || '',
              panditName: normalizedService?.selectedPandit?.name || '',
              panditPhone: normalizedService?.selectedPandit?.phone || '',
              payload: bookingData
            },
            { openWhatsApp: false }
          );
        } catch (error) {
        }
      }

      onConfirmBooking({
        ...bookingData,
        leadId: leadResult?.lead?.id || null,
        leadCaptured: Boolean(leadResult?.lead)
      });
      onClose();
      // Reset form
      setCustomerInfo({ name: '', email: '', phone: '', address: '' });
      setSelectedDate('');
      setSelectedTime('');
      setSelectedPackage('basic');
      setErrors({});
      setLeadSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose}></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className="text-3xl">{normalizedService.image}</div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{normalizedService.title}</h2>
                  <p className="text-gray-600">Complete your booking details</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left Column - Service Details */}
              <div>
                {/* Service Info */}
                <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Service Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium">{normalizedService.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rating:</span>
                      <span className="font-medium">⭐ {normalizedService.rating} ({normalizedService.reviews} reviews)</span>
                    </div>
                  </div>
                  <p className="text-gray-700 mt-4">{normalizedService.description}</p>
                </div>

                {/* Package Selection */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Package</h3>
                  <div className="space-y-3">
                    {Object.entries(packages).map(([key, pkg]) => (
                      <label
                        key={key}
                        className={`block p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedPackage === key
                            ? 'border-rose-500 bg-rose-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="package"
                          value={key}
                          checked={selectedPackage === key}
                          onChange={(e) => setSelectedPackage(e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{pkg.name}</div>
                            <div className="text-sm text-gray-600 mt-1">
                              {pkg.features.slice(0, 3).join(', ')}
                              {pkg.features.length > 3 && ` +${pkg.features.length - 3} more`}
                            </div>
                          </div>
                          <div className="text-lg font-bold text-rose-600 ml-4">
                            {formatPrice(pkg.price)}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* What's Included */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 mb-3">What's Included in {packages[selectedPackage].name}</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {packages[selectedPackage].features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        <span className="text-rose-600">✓</span>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Booking Form */}
              <div>
                {/* Customer Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={customerInfo.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
                          errors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your full name"
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={customerInfo.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your email"
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={customerInfo.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your phone number"
                      />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <textarea
                        value={customerInfo.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                        placeholder="Enter your address (optional)"
                      />
                    </div>
                  </div>
                </div>

                {/* Date & Time Selection */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Date & Time</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date *
                      </label>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
                          errors.date ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Time *
                      </label>
                      <select
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
                          errors.time ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select time</option>
                        {timeSlots.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                      {errors.time && <p className="text-red-500 text-xs mt-1">{errors.time}</p>}
                    </div>
                  </div>
                </div>

                {/* Total Price Summary */}
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-gray-900">Total Amount</div>
                      <div className="text-sm text-gray-600">{packages[selectedPackage].name}</div>
                    </div>
                    <div className="text-2xl font-bold text-rose-600">
                      {formatPrice(packages[selectedPackage].price)}
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 mt-2">
                    *Final price may vary based on specific requirements
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleConfirm}
                    disabled={leadSubmitting}
                    className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  >
                    {leadSubmitting ? 'Processing...' : `Confirm Booking - ${formatPrice(packages[selectedPackage].price)}`}
                  </button>
                  
                  {/* WhatsApp Quick Booking Button */}
                  <button
                    onClick={() => {
                      window.open(getWhatsAppBookingLink(normalizedService.title, customerInfo.phone, customerInfo.email), '_blank');
                    }}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <span>💬</span>
                    <span>Book via WhatsApp</span>
                  </button>
                  
                  <button
                    onClick={handleClose}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-6 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>

                {/* Service Guarantee */}
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600">🛡️</span>
                    <div className="text-sm">
                      <div className="font-medium text-green-800">100% Service Guarantee</div>
                      <div className="text-green-700 text-xs">Professional service with traditional authenticity</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
